// @file Mobile app related state actions and mutations.
// * Note: we don't want to call postMessage directly here.
// * We want to instead set the state and let plugins/components respond and act
// * accordingly with native bridge.
import appCan from 'bits/app_can'
import { circularSafeStringify } from 'bits/json_stringify'
import { postSurfaceState } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { LinkType } from 'native_bridge/types'
import { Post, UserId } from 'types'
import { ActionContext, Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'
interface NativeAppOpenLink {
  url: string
  linkType: LinkType
  target?: '_blank' | '_top' | '_self'
  contentCategory: string
  contentSubcategory: string
}

interface AppState {
  linkToOpen: NativeAppOpenLink | null
  // Reference: https://linear.app/padlet/issue/PRO-302/more-permanent-fix-for-drafts-native-component-covering-plus-button
  // the native component for draft posts may cover the bottom of the screen, preventing user from interacting with buttons at the bottom of shelf sections
  // => let the app decide how much padding is needed so the buttons are not covered by the native component
  shelfSectionBottomPaddingPx: number
}

type NativeActionContext = ActionContext<AppState, RootState>

const NativeApp: Module<AppState, RootState> = {
  namespaced: true,
  state: {
    linkToOpen: null,
    shelfSectionBottomPaddingPx: 0,
  },
  getters: {
    expandedPost: (state, getters, rootState, rootGetters): Post | undefined => {
      return rootGetters['postAction/expandedPost']
    },
    shelfSectionBottomPaddingPx: (state): number => state.shelfSectionBottomPaddingPx,
  },
  mutations: {
    NATIVE_APP_OPEN_LINK(state, nativeAppOpenLink: NativeAppOpenLink): void {
      state.linkToOpen = nativeAppOpenLink
    },
    NATIVE_APP_LINK_OPENED(state): void {
      state.linkToOpen = null
    },
    NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING(state, { new_padding }: { new_padding: number }): void {
      state.shelfSectionBottomPaddingPx = new_padding
    },
  },
  actions: {
    openLink({ commit }, nativeAppOpenLink: NativeAppOpenLink): void {
      commit('NATIVE_APP_OPEN_LINK', nativeAppOpenLink)
    },
    clearOpenLink({ commit }): void {
      commit('NATIVE_APP_LINK_OPENED')
    },
    hideAllPanelsAndMenus({ dispatch, rootState }): void {
      dispatch('settings/hideSettingsPanel', null, { root: true })
      dispatch('share/resetSharePanel', null, { root: true })
      dispatch('hideRemakePanel', null, { root: true })
      dispatch('hideConvertLayoutPanel', null, { root: true })
      dispatch('postAction/endPostCopy', null, { root: true })
      dispatch('postAction/endPostTransfer', null, { root: true })
      if (rootState.postAction && rootState.postAction.postUnderActionCid) {
        dispatch('postAction/hidePostActionMenu', null, { root: true })
      }
      if (rootState.section && rootState.section.actionMenuSectionId) {
        dispatch('section/hideSectionActionMenu', null, { root: true })
      }
      if (rootState.comment && rootState.comment.actionMenuCommentId) {
        dispatch('comment/hideCommentActionMenu', null, { root: true })
      }
    },
    updateShelfSectionBottomPadding({ commit }, payload: { new_padding: number }): void {
      commit('NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING', payload)
    },
    // data that will be sent only once should be put in transientPayload
    postSurfaceState({ rootGetters }, transientPayload?: { collaborator_just_left_user_id?: UserId }): void {
      if (!rootGetters['isApp']) {
        return
      }
      // Versioning of the shape of `expanded_post_data` is tracked here https://stackoverflow.com/c/padlet/articles/1921
      const expandedPostData = appCan('useNativeExpandedPostV1')
        ? {
            posts: rootGetters['post/postsArray'].map((p: Post): any => ({
              id: p.id,
              cid: p.cid,
              has_attachment: !!p.attachment,
            })),
            expanded_post: {
              ...rootGetters['postAction/expandedPost'],
              attachment_frame_url: rootGetters['postAction/expandedPostMobileFrameUrl'],
            },
          }
        : {}
      const plainObjectAppState = JSON.parse(
        circularSafeStringify({
          wall_sections: rootGetters['section/sortedSections'],
          oauth_token: rootGetters['oauthToken'],
          reactions: rootGetters['reaction/reactions'],
          wall_reaction_data: rootGetters['reactionData'],
          expanded_post_data: expandedPostData,
          // amICollaborator is to show/hide the "Leave padlet" button in the mobile app
          am_i_collaborator: rootGetters['amICollaborator'],
          collaborator_just_left_user_id: transientPayload?.collaborator_just_left_user_id,
        }),
      )
      postMessage(
        postSurfaceState({
          ...plainObjectAppState,
        }),
      )
    },
  },
}

export default NativeApp
